import { IExpenseStorageService } from './IExpenseStorageService';

const STORAGE_KEYS = {
  EXPENSES: 'financeTracker_expenses',
  INCOME: 'financeTracker_income'
};

function incomeKey(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

// Days of month a recurring expense occurs on. Falls back to the start
// date's own day for expenses recorded before multi-day recurrence existed.
function getRecurringDays(expense) {
  if (Array.isArray(expense.recurringDays) && expense.recurringDays.length > 0) {
    return expense.recurringDays;
  }
  return [new Date(expense.date + 'T00:00:00').getDate()];
}

// Projects a recurring expense into a specific year/month, returning one
// instance per configured day, each clamped to the last day of that month
// (e.g. a 31st occurrence projects to the 30th in a 30-day month).
function projectRecurringInstances(expense, year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  const paddedMonth = String(month).padStart(2, '0');
  return getRecurringDays(expense).map(day => {
    const clampedDay = Math.min(day, lastDay);
    const paddedDay = String(clampedDay).padStart(2, '0');
    return { ...expense, date: `${year}-${paddedMonth}-${paddedDay}` };
  });
}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * LocalStorage implementation of IExpenseStorageService
 */
export class LocalStorageExpenseService extends IExpenseStorageService {
  async getExpensesForMonth(year, month) {
    const all = this._getExpenses();
    const result = [];

    for (const expense of all) {
      const expDate = new Date(expense.date + 'T00:00:00');
      const expYear = expDate.getFullYear();
      const expMonth = expDate.getMonth() + 1;

      if (!expense.recurring) {
        if (expYear === year && expMonth === month) {
          result.push(expense);
        }
      } else {
        const startedBefore = expYear < year || (expYear === year && expMonth <= month);
        if (startedBefore) {
          result.push(...projectRecurringInstances(expense, year, month));
        }
      }
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  async getExpensesForYear(year) {
    const all = this._getExpenses();
    const result = [];

    for (const expense of all) {
      const expDate = new Date(expense.date + 'T00:00:00');
      const expYear = expDate.getFullYear();

      if (!expense.recurring) {
        if (expYear === year) {
          result.push(expense);
        }
      } else {
        const expMonth = expDate.getMonth() + 1;
        for (let month = 1; month <= 12; month++) {
          const startedBefore = expYear < year || (expYear === year && expMonth <= month);
          if (startedBefore) {
            result.push(...projectRecurringInstances(expense, year, month));
          }
        }
      }
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  async addExpense(data) {
    const all = this._getExpenses();
    const expense = { ...data, id: generateId() };
    all.push(expense);
    this._saveExpenses(all);
    return expense;
  }

  async updateExpense(expense) {
    const all = this._getExpenses();
    const idx = all.findIndex(e => e.id === expense.id);
    if (idx === -1) throw new Error(`Expense ${expense.id} not found`);
    all[idx] = expense;
    this._saveExpenses(all);
    return expense;
  }

  async deleteExpense(id) {
    const all = this._getExpenses().filter(e => e.id !== id);
    this._saveExpenses(all);
  }

  async getIncome(year, month) {
    const map = this._getIncomeMap();
    // Walk backwards up to 24 months to find the most recent set value
    let y = year, m = month;
    for (let i = 0; i < 24; i++) {
      const val = map[incomeKey(y, m)];
      if (val !== undefined) return val;
      m--;
      if (m === 0) { m = 12; y--; }
    }
    return 0;
  }

  async setIncome(year, month, amount) {
    const map = this._getIncomeMap();
    map[incomeKey(year, month)] = amount;
    try {
      localStorage.setItem(STORAGE_KEYS.INCOME, JSON.stringify(map));
    } catch (error) {
      console.error('Error saving income to localStorage:', error);
      throw error;
    }
  }

  async exportData() {
    return { expenses: this._getExpenses(), income: this._getIncomeMap() };
  }

  async importData(data) {
    this._saveExpenses(data.expenses);
    try {
      localStorage.setItem(STORAGE_KEYS.INCOME, JSON.stringify(data.income));
    } catch (error) {
      console.error('Error importing income to localStorage:', error);
      throw error;
    }
  }

  // Private helper methods
  _getExpenses() {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.EXPENSES);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      console.error('Error parsing expenses from localStorage:', error);
      return [];
    }
  }

  _saveExpenses(expenses) {
    try {
      localStorage.setItem(STORAGE_KEYS.EXPENSES, JSON.stringify(expenses));
    } catch (error) {
      console.error('Error saving expenses to localStorage:', error);
      throw error;
    }
  }

  _getIncomeMap() {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.INCOME);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.error('Error parsing income from localStorage:', error);
      return {};
    }
  }
}
